import { KeyboardNavigationProvider } from '@wkda/styleguide';
import setFieldData from 'final-form-set-field-data';
import createSubmitDecorator from 'final-form-submit-listener';
import React, { cloneElement, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deepForEach, deepMap } from 'react-children-utilities';
import { unstable_batchedUpdates } from 'react-dom';
import { Form } from 'react-final-form';
import { getLastStepFromSaved } from './stepHelpers';
import { useStepAnchors } from './useStepAnchors';
import { useWizardStore } from './wizardStore';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function setFieldValue(args, state, _ref) {
  let {
    changeValue
  } = _ref;
  const [name, value] = args;
  const field = state.fields[name];
  if (field) {
    changeValue(state, name, () => value);
  }
}
const wizardContext = /*#__PURE__*/createContext(undefined);
export default function Wizard(_ref2) {
  let {
    children,
    initialValues,
    onSubmit,
    validate,
    totalSteps: _totalSteps,
    firstStepShift = 0,
    getErrorsPerStep,
    topContent = null,
    bottomContent = null,
    onValidationError = () => {},
    onSubmitSuccess = () => {},
    onSubmitError = () => {},
    onBeforeSubmit = () => {},
    onStepChange = () => {},
    parseResponse = (_, values) => {
      return values;
    },
    childrenRenderFn = (children, _ref3) => {
      let {
        totalSteps
      } = _ref3;
      return deepMap(children, generateChildrenFn(totalSteps));
    },
    decorators = []
  } = _ref2;
  const [values, _setInitialValues] = useState(() => {
    return initialValues;
  });
  const totalChildrenSteps = getWizardPages(children).length;
  const totalSteps = _totalSteps || totalChildrenSteps;
  const [status, setStatus] = useState('idle');
  const [savedSteps, setSavedSteps] = useState(() => {
    return validatePagesSteps(getErrorsPerStep(initialValues));
  });
  const [currentStep, setStep] = useState(() => getLastStepFromSaved(savedSteps));
  const isLastStep = currentStep === totalSteps - 1;
  const setInitialValues = useCallback(function $setInitialValues(payload) {
    _setInitialValues(parseResponse(payload, {}));
  }, [parseResponse]);
  useEffect(() => {
    const savedSteps = validatePagesSteps(getErrorsPerStep(initialValues));
    setSavedSteps(savedSteps);
  }, [getErrorsPerStep, initialValues]);
  useStepAnchors({
    currentStep,
    setStep,
    savedSteps
  });

  //TODO: Check why this is being called 10 times on first render
  function _validate(values) {
    const result = validate(currentStep, values);
    return result;
  }
  function transitionToNextStep() {
    unstable_batchedUpdates(() => {
      setSavedSteps(function onSetSavedSteps(prev) {
        const p = prev.slice();
        p[currentStep] = true;
        return p;
      });
      setStep(function onSetStep(currentPage) {
        return Math.min(currentPage + 1, totalSteps - 1);
      });
    });
  }
  const wizardAPI = useMemo(() => {
    return {
      firstStepShift,
      currentStep,
      setStep,
      savedSteps,
      totalSteps,
      status,
      setInitialValues,
      isLastStep: currentStep === totalSteps - 1
    };
  }, [firstStepShift, currentStep, savedSteps, totalSteps, status, setInitialValues]);
  const wizardAPIRef = useRef(wizardAPI);
  const onSubmitErrorRef = useRef(onValidationError);
  const onBeforeSubmitRef = useRef(onBeforeSubmit);
  const getErrorsPerStepRef = useRef(getErrorsPerStep);
  useEffect(() => {
    wizardAPIRef.current = wizardAPI;
    onSubmitErrorRef.current = onValidationError;
    onBeforeSubmitRef.current = onBeforeSubmit;
    getErrorsPerStepRef.current = getErrorsPerStep;
  });
  const setWizard = useWizardStore(state => state.setWizard);
  useEffect(() => {
    setWizard(wizardAPIRef.current);
  }, [setWizard]);
  useEffect(function stepChange() {
    onStepChange(currentStep, isLastStep);
  }, [currentStep, isLastStep, onStepChange]);
  const submitListener = useMemo(() => {
    return createSubmitDecorator({
      beforeSubmit(form) {
        return onBeforeSubmitRef.current(form, {
          ...wizardAPIRef.current,
          setInitialValues: _setInitialValues
        });
      },
      afterSubmitFailed(form) {
        const v = form.getState().values;
        onSubmitErrorRef.current(form, wizardAPIRef.current, getErrorsPerStepRef.current(v));
      }
    });
  }, []);
  function onSubmitHandler(values, form) {
    setStatus('loading');
    return onSubmit(values, currentStep, isLastStep, form).then(r => {
      _setInitialValues(parseResponse(r, values));
      onSubmitSuccess(currentStep, isLastStep, r, setStep, values);
      transitionToNextStep();
      setStatus('loadingSuccess');
    }).catch(e => {
      setStatus('loadingFailed');
      console.log('[On Wizard submit error]');
      const errors = getErrorsPerStepRef.current(form.getState().values);
      const hasValidationErrors = errors.some(e => Object.keys(e).length > 0);
      if (hasValidationErrors) {
        onSubmitErrorRef.current(form, wizardAPIRef.current, getErrorsPerStepRef.current(form.getState().values));
      }
      return onSubmitError(currentStep, isLastStep, e, setStep, wizardAPI, values);
    });
  }
  return /*#__PURE__*/_jsx(wizardContext.Provider, {
    value: wizardAPI,
    children: /*#__PURE__*/_jsx(Form, {
      subscription: {
        errors: false,
        values: true
      },
      validate: _validate,
      onSubmit: onSubmitHandler,
      initialValues: values,
      mutators: {
        setFieldData,
        setFieldValue
      },
      decorators: [submitListener, ...decorators],
      children: function FinalFormRender(_ref4) {
        let {
          handleSubmit
        } = _ref4;
        return /*#__PURE__*/_jsxs(KeyboardNavigationProvider, {
          children: [topContent, /*#__PURE__*/_jsx("div", {
            className: "form-wizard__body",
            children: /*#__PURE__*/_jsx("form", {
              method: "POST",
              onSubmit: handleSubmit,
              noValidate: true,
              children: childrenRenderFn(children, wizardAPI)
            })
          }), bottomContent]
        });
      }
    })
  });
}
function validatePagesSteps() {
  let errors = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const stepsAvailability = errors.map(obj => {
    return objectIsEmpty(obj);
  });
  const indexOfLastNotFinishedStep = stepsAvailability.indexOf(false);
  return errors.map((_, index) => indexOfLastNotFinishedStep === -1 || index < indexOfLastNotFinishedStep);
}
function getWizardPages(children) {
  let pages = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  deepForEach(children, function getPages(page) {
    if (page?.type === WizardPage) {
      pages.push(page);
    }
  });
  return pages;
}
export function generateChildrenFn(totalSteps) {
  let idx = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  function renderChildren(child) {
    let children = [];
    if (child.type === WizardPage) {
      if (idx < totalSteps) {
        children.push(/*#__PURE__*/cloneElement(child, {
          step: idx + 1
        }));
      }
      idx += 1;
    } else {
      children.push(child);
    }
    return children;
  }
  return renderChildren;
}
const wizardPageContext = /*#__PURE__*/createContext(undefined);
export function useWizardPage() {
  const ctx = useContext(wizardPageContext);
  if (!ctx) {
    throw new Error('useWizardPage hook needs to be inside a WizardPage (WizardPage)');
  }
  return ctx;
}
function WizardPage(_ref5) {
  let {
    children,
    step
  } = _ref5;
  return /*#__PURE__*/_jsx(wizardPageContext.Provider, {
    value: step,
    children: children
  });
}
Wizard.Page = WizardPage;
export function useWizard() {
  const wizard = useContext(wizardContext);
  if (!wizard) {
    throw new Error('useWizard hook needs to be inside a WizardProvider (Wizard)');
  }
  return wizard;
}
function objectIsEmpty() {
  let obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.keys(obj).length === 0;
}